import type {
  ProductRecommendationFragment,
  RecommendationExternalInsuranceFragment,
  RecommendationFragment,
} from '@/services/graphql/generated'
import {
  type OfferRecommendationFragment,
  type ProductRecommendationsQuery,
  useProductRecommendationsQuery,
} from '@/services/graphql/generated'
import { useShopSessionId } from '@/services/shopSession/ShopSessionContext'
import { Features } from '@/utils/Features'

export type OfferRecommendation = {
  offer: OfferRecommendationFragment
  product: ProductRecommendationFragment
  externalInsurance: RecommendationExternalInsuranceFragment | null
}

export type ProductRecommendation = {
  offer: null
  product: ProductRecommendationFragment
  externalInsurance: RecommendationExternalInsuranceFragment
  priceIntentData: RecommendationFragment['priceIntentData']
}

export type Recommendation = OfferRecommendation | ProductRecommendation

export function isOfferRecommendation(rec: Recommendation): rec is OfferRecommendation {
  return rec.offer != null
}

export function isProductRecommendation(rec: Recommendation): rec is ProductRecommendation {
  return rec.offer == null
}

type UseRecommendationsResult = {
  offerRecommendations: Array<OfferRecommendation>
  productRecommendations: Array<ProductRecommendation>
  loading: boolean
  refetch: () => void
}

export const useRecommendations = (customShopSessionId?: string): UseRecommendationsResult => {
  const currentShopSessionId = useShopSessionId()
  const shopSessionId = customShopSessionId ?? currentShopSessionId

  const result = useProductRecommendationsQuery({
    fetchPolicy: 'cache-and-network',
    variables: shopSessionId ? { shopSessionId } : undefined,
    skip: shopSessionId == null,
  })

  const allRecs = result.data ? getRecommendations(result.data) : []

  if (!Features.enabled('EXTERNAL_INSURANCE_CROSS_SELL')) {
    const accidentOnly = allRecs.filter(isOfferRecommendation).filter((rec) => rec.product.name === 'SE_ACCIDENT')
    return {
      offerRecommendations: accidentOnly,
      productRecommendations: [],
      loading: result.loading,
      refetch: result.refetch,
    }
  }

  return {
    offerRecommendations: allRecs.filter(isOfferRecommendation),
    productRecommendations: allRecs.filter(isProductRecommendation),
    loading: result.loading,
    refetch: result.refetch,
  }
}

const getRecommendations = (data: ProductRecommendationsQuery): Array<Recommendation> => {
  return data.shopSession.recommendations.reduce<Array<Recommendation>>((acc, item) => {
    if (item.offer != null) {
      acc.push({
        product: item.product,
        offer: item.offer,
        externalInsurance: item.externalInsurance ?? null,
      })
    } else if (item.externalInsurance != null) {
      acc.push({
        product: item.product,
        offer: null,
        externalInsurance: item.externalInsurance,
        priceIntentData: item.priceIntentData ?? null,
      })
    }
    return acc
  }, [])
}
